'use client';

import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import type { FilterTier } from '@/lib/search-gating';
import type { FilterParams } from '@/lib/search-filter-params';
import type { NaceCode } from './searchSidebarOptions';
import { RegisterFilterGroup } from './filter-groups/RegisterFilterGroup';
import { PeopleFilterGroup } from './filter-groups/PeopleFilterGroup';
import { SizeFilterGroup } from './filter-groups/SizeFilterGroup';
import { IndustryFilterGroup } from './filter-groups/IndustryFilterGroup';
import { LocationFilterGroup } from './filter-groups/LocationFilterGroup';
import { ProfileFilterGroup } from './filter-groups/ProfileFilterGroup';
import { SidebarFooter } from './filter-groups/SidebarFooter';
import { KeywordSearchField } from './filter-groups/KeywordSearchField';
import { getAgeRangeLabel, removeSet, sidebarAsideClassName, toggleSet } from './searchSidebarHelpers';
import { SidebarMobileHeader } from './filter-groups/SidebarMobileHeader';
import { useFilterPickerState, useSearchFacets, useSidebarOptions } from './useSidebarPickers';
import { countActiveFilters, useCollectFilters, useFilterGroupClears, useSidebarFilterValues } from './useSidebarFilterState';
import { useSidebarKeywordSearch } from './useSidebarKeywordSearch';

interface CompanySearchSidebarProps {
  naceCodes: NaceCode[];
  cityList: string[];
  tagList: string[];
  onSubmit: (filters: FilterParams) => void;
  sort?: string;
  offset?: number;
  limit?: number;
  totalLimit?: number;
  onTotalLimitChange?: (limit: number) => void;
  /** Show the fiscal-year picker. The value itself rides in FilterParams. */
  showFinancialYear?: boolean;
  total?: number;
  onOffsetChange?: (offset: number) => void;
  filterParams?: FilterParams;
  isOpen?: boolean;
  onClose?: () => void;
  onCountryChange?: (country: string) => void;
  /** When true, the sidebar renders as a fullscreen overlay on mobile (with
   *  a top close button and the company-search input visible). Used by the
   *  /search page; other consumers (e.g. radar) keep the legacy inline
   *  dropdown behavior. */
  mobileFullscreen?: boolean;
  /**
   * Which filters this visitor may use — and, at `'public'`, whether the
   * mobile pagination block is offered at all.
   *
   * One prop rather than the `isAuthenticated` + `isPro` pair it replaces:
   * the two were separate booleans encoding a single ordered value, so every
   * consumer had to re-derive the tier and could disagree about it.
   */
  tier?: FilterTier;
  hideCompanySearch?: boolean;
  hidePersonSearch?: boolean;
  hideNaceCodes?: boolean;
  /**
   * Save the staged filter set.
   *
   * The sidebar holds *staged* state while the results on screen come from
   * the last applied search, so the page applies the filters as part of
   * saving them — otherwise a user who tweaked a control without pressing
   * Search would silently save something other than what they are looking
   * at. Absent (radar, /listen) the control is not rendered at all.
   */
  onSaveSearch?: (filters: FilterParams) => void;
  /** The user's saved searches, for the quick-load menu. */
  savedSearches?: { id: number; name: string; filters: FilterParams }[];
  onLoadSavedSearch?: (id: number) => void;
  /** True when the staged filters already match a saved search. */
  isCurrentSearchSaved?: boolean;
}

export default function CompanySearchSidebar({
  naceCodes,
  cityList,
  tagList,
  onSubmit,
  offset = 0,
  totalLimit = 50,
  showFinancialYear = false,
  total = 0,
  onOffsetChange,
  filterParams,
  isOpen = true,
  onClose,
  onCountryChange,
  mobileFullscreen = false,
  tier = 'public',
  hideCompanySearch = false,
  hidePersonSearch = false,
  hideNaceCodes = false,
  onSaveSearch,
  savedSearches,
  onLoadSavedSearch,
  isCurrentSearchSaved = false,
}: CompanySearchSidebarProps) {
  const { t, i18n } = useTranslation();
  const fmtLocale = i18n.resolvedLanguage || i18n.language || 'en';
  // Hook order mirrors the effect order the inline version had: facets fetch,
  // CPV + court fetches, the filterParams sync, then the autocomplete effects.
  const { extents, facets, facetCount, facetSum } = useSearchFacets();
  const {
    cityOpen, setCityOpen, citySearch, setCitySearch, bundeslandOpen, setBundeslandOpen,
    ageOpen, setAgeOpen, naceCodesOpen, setNaceCodesOpen, naceSearch, setNaceSearch,
    expandedNaceNodes, toggleNaceExpanded, naceListRef, industryOpen, setIndustryOpen,
    tagsOpen, setTagsOpen, tagSearch, setTagSearch, cpvCodesOpen, setCpvCodesOpen,
    cpvSearch, setCpvSearch, cpvCodesList, cpvListRef, rechtsformOpen, setRechtsformOpen,
    yearPickerOpen, setYearPickerOpen, registerTypeOpen, setRegisterTypeOpen,
    registerCourtOpen, setRegisterCourtOpen, registerCourtSearch, setRegisterCourtSearch,
    courtList, openFilterGroup, handleGroupOpenChange, membershipsOpen, setMembershipsOpen,
  } = useFilterPickerState();

  const preserveCountryEdits = Boolean(onCountryChange);
  const filterValues = useSidebarFilterValues(filterParams, preserveCountryEdits);
  const {
    searchInput, setSearchInput, country, selectedCities, setSelectedCities,
    currentRegisterId, setCurrentRegisterId, currentRegisterCourt, setCurrentRegisterCourt,
    currentRegisterType, setCurrentRegisterType, currentEuid, setCurrentEuid,
    hasYearlyMetadata, setHasYearlyMetadata, selectedBundesland, setSelectedBundesland,
    revenueMin, setRevenueMin, revenueMax, setRevenueMax, revenueIncludeNull, setRevenueIncludeNull,
    aktivaMin, setAktivaMin, aktivaMax, setAktivaMax, financialYear, setFinancialYear,
    profitMin, setProfitMin, profitMax, setProfitMax, employeesMin, setEmployeesMin,
    employeesMax, setEmployeesMax, selectedAgeRanges, setSelectedAgeRanges,
    selectedNaceCodes, setSelectedNaceCodes, selectedIndustries, toggleIndustry,
    foundedAtFrom, setFoundedAtFrom, foundedAtTo, setFoundedAtTo, tags, setTags,
    personName, setPersonName, cpvCode, setCpvCode, selectedRechtsformen, setSelectedRechtsformen,
    selectedMemberships, setSelectedMemberships, selectedLegalStatus, setSelectedLegalStatus,
    selectedCompanySize, setSelectedCompanySize, selectedTenderRole, setSelectedTenderRole,
    familyManaged, setFamilyManaged, founderLed, setFounderLed, successionRisk, setSuccessionRisk,
    leiCode, setLeiCode, ustIdnr, setUstIdnr, hasWebsite, setHasWebsite, hasEmail, setHasEmail,
  } = filterValues;

  const {
    locationOptions, allIndustries, rechtsformOptions, membershipOptions, registerTypeOptions,
    lockOf, groupLock, naceCodesHaveChildren, filteredNaceCodes, yearOptions,
  } = useSidebarOptions({
    country, facetCount, facetSum, i18n, fmtLocale, naceCodes, naceSearch, expandedNaceNodes, tier,
  });

  const collectFilters = useCollectFilters(filterValues);
  const { locationCount, industryCount, sizeCount, profileCount, peopleCount, registerCount } =
    countActiveFilters(filterValues);
  const { clearLocation, clearIndustry, clearSize, clearProfile, clearPeople, clearRegister } =
    useFilterGroupClears(filterValues);

  const {
    autocompleteRef, suggestions, showSuggestions, setShowSuggestions, suggestionsLoading,
    sidebarActiveIndex, setSidebarActiveIndex, keyboardNavRef, lastSearches, showLastSearches,
    setShowLastSearches, fetchLastSearches, discardLastSearch, fetchSuggestions,
    handleFormSubmit, handleSuggestionClick,
  } = useSidebarKeywordSearch(onSubmit, collectFilters);

  return (
    <aside
      id="search-filters"
      aria-label={t('search.filters')}
      className={sidebarAsideClassName(mobileFullscreen, isOpen)}
    >
      {/* Mobile-only top bar with a close button — fullscreen-overlay mode
          only. lg:hidden so desktop keeps the inline-panel chrome. */}
      {mobileFullscreen && onClose && (
        <SidebarMobileHeader
          t={t}
          onClose={onClose}
        />
      )}
      {/* Inner wrapper keeps content at fixed width during desktop collapse */}
      <div
        className={cn(
          'flex flex-col w-full lg:w-[380px] lg:min-w-[380px]',
          // In fullscreen-overlay mode the wrapper needs to fill the aside so
          // the footer Search button stays pinned at the bottom. Legacy mode
          // keeps the natural-height layout for radar's inline dropdown.
          mobileFullscreen ? 'flex-1 min-h-0' : 'lg:h-full',
        )}
      >
        {/* Scrollable Filters. `overscroll-contain` stops iOS Safari from
            bubble-scrolling the page underneath when the user hits the top/
            bottom of the list inside the mobile filter overlay. */}
        <div className="flex-1 overflow-y-auto overscroll-contain min-h-0 p-4 flex flex-col">
          {/* Top-level unified keyword input — binds to `q` (the backend's
              unified keyword field). Funnels into eu_id → company-name →
              person-name resolution server-side. Autocomplete dropdown only
              shows company suggestions per /api/companies/suggest; person /
              euId terms are submitted via Enter. */}
          {!hideCompanySearch && (
            <KeywordSearchField
              autocompleteRef={autocompleteRef}
              t={t}
              searchInput={searchInput}
              setSearchInput={setSearchInput}
              setShowLastSearches={setShowLastSearches}
              fetchSuggestions={fetchSuggestions}
              setShowSuggestions={setShowSuggestions}
              fetchLastSearches={fetchLastSearches}
              showSuggestions={showSuggestions}
              suggestions={suggestions}
              keyboardNavRef={keyboardNavRef}
              setSidebarActiveIndex={setSidebarActiveIndex}
              sidebarActiveIndex={sidebarActiveIndex}
              handleSuggestionClick={handleSuggestionClick}
              handleFormSubmit={handleFormSubmit}
              showLastSearches={showLastSearches}
              lastSearches={lastSearches}
              suggestionsLoading={suggestionsLoading}
              discardLastSearch={discardLastSearch}
            />
          )}

          {/* ─────────────────────── Filter groups ─────────────────────── */}

          {/* Group 1 — Company profile: Rechtsform, founding window,
              memberships, contactability. First and open by default: it is
              the group a visitor can use in full without an account. */}
          <ProfileFilterGroup
            t={t}
            profileCount={profileCount}
            clearProfile={clearProfile}
            openFilterGroup={openFilterGroup}
            handleGroupOpenChange={handleGroupOpenChange}
            groupLock={groupLock}
            rechtsformOpen={rechtsformOpen}
            setRechtsformOpen={setRechtsformOpen}
            selectedRechtsformen={selectedRechtsformen}
            removeSet={removeSet}
            setSelectedRechtsformen={setSelectedRechtsformen}
            rechtsformOptions={rechtsformOptions}
            toggleSet={toggleSet}
            facetSum={facetSum}
            foundedAtFrom={foundedAtFrom}
            setFoundedAtFrom={setFoundedAtFrom}
            handleFormSubmit={handleFormSubmit}
            foundedAtTo={foundedAtTo}
            setFoundedAtTo={setFoundedAtTo}
            lockOf={lockOf}
            membershipsOpen={membershipsOpen}
            setMembershipsOpen={setMembershipsOpen}
            selectedMemberships={selectedMemberships}
            setSelectedMemberships={setSelectedMemberships}
            membershipOptions={membershipOptions}
            facetCount={facetCount}
            selectedLegalStatus={selectedLegalStatus}
            setSelectedLegalStatus={setSelectedLegalStatus}
            selectedCompanySize={selectedCompanySize}
            setSelectedCompanySize={setSelectedCompanySize}
            selectedTenderRole={selectedTenderRole}
            setSelectedTenderRole={setSelectedTenderRole}
            hasWebsite={hasWebsite}
            setHasWebsite={setHasWebsite}
            hasEmail={hasEmail}
            setHasEmail={setHasEmail}
          />

          {/* Group 2 — Location: City + Bundesland */}
          <LocationFilterGroup
            t={t}
            locationCount={locationCount}
            clearLocation={clearLocation}
            openFilterGroup={openFilterGroup}
            handleGroupOpenChange={handleGroupOpenChange}
            groupLock={groupLock}
            lockOf={lockOf}
            cityOpen={cityOpen}
            setCityOpen={setCityOpen}
            setCitySearch={setCitySearch}
            selectedCities={selectedCities}
            removeSet={removeSet}
            setSelectedCities={setSelectedCities}
            citySearch={citySearch}
            cityList={cityList}
            toggleSet={toggleSet}
            facetCount={facetCount}
            onCountryChange={onCountryChange}
            country={country}
            bundeslandOpen={bundeslandOpen}
            setBundeslandOpen={setBundeslandOpen}
            selectedBundesland={selectedBundesland}
            i18n={i18n}
            setSelectedBundesland={setSelectedBundesland}
            locationOptions={locationOptions}
          />

          {/* Group 3 — Industry & activity: NACE, Industry, Tags, CPV */}
          <IndustryFilterGroup
            t={t}
            industryCount={industryCount}
            clearIndustry={clearIndustry}
            openFilterGroup={openFilterGroup}
            handleGroupOpenChange={handleGroupOpenChange}
            groupLock={groupLock}
            lockOf={lockOf}
            industryOpen={industryOpen}
            setIndustryOpen={setIndustryOpen}
            selectedIndustries={selectedIndustries}
            allIndustries={allIndustries}
            toggleIndustry={toggleIndustry}
            facetCount={facetCount}
            hideNaceCodes={hideNaceCodes}
            naceCodesOpen={naceCodesOpen}
            setNaceCodesOpen={setNaceCodesOpen}
            setNaceSearch={setNaceSearch}
            selectedNaceCodes={selectedNaceCodes}
            naceCodes={naceCodes}
            removeSet={removeSet}
            setSelectedNaceCodes={setSelectedNaceCodes}
            naceSearch={naceSearch}
            naceListRef={naceListRef}
            filteredNaceCodes={filteredNaceCodes}
            naceCodesHaveChildren={naceCodesHaveChildren}
            expandedNaceNodes={expandedNaceNodes}
            toggleSet={toggleSet}
            toggleNaceExpanded={toggleNaceExpanded}
            tagsOpen={tagsOpen}
            setTagsOpen={setTagsOpen}
            setTagSearch={setTagSearch}
            tags={tags}
            setTags={setTags}
            tagSearch={tagSearch}
            tagList={tagList}
            cpvCodesOpen={cpvCodesOpen}
            setCpvCodesOpen={setCpvCodesOpen}
            setCpvSearch={setCpvSearch}
            cpvCode={cpvCode}
            cpvCodesList={cpvCodesList}
            setCpvCode={setCpvCode}
            cpvSearch={cpvSearch}
            cpvListRef={cpvListRef}
          />

          {/* Group 4 — Size & financials: Revenue, Bilanzsumme, Profit,
              Employees, filing year, filings-only */}
          <SizeFilterGroup
            t={t}
            sizeCount={sizeCount}
            clearSize={clearSize}
            openFilterGroup={openFilterGroup}
            handleGroupOpenChange={handleGroupOpenChange}
            groupLock={groupLock}
            lockOf={lockOf}
            revenueMin={revenueMin}
            revenueMax={revenueMax}
            setRevenueMin={setRevenueMin}
            setRevenueMax={setRevenueMax}
            extents={extents}
            revenueIncludeNull={revenueIncludeNull}
            setRevenueIncludeNull={setRevenueIncludeNull}
            aktivaMin={aktivaMin}
            aktivaMax={aktivaMax}
            setAktivaMin={setAktivaMin}
            setAktivaMax={setAktivaMax}
            profitMin={profitMin}
            profitMax={profitMax}
            setProfitMin={setProfitMin}
            setProfitMax={setProfitMax}
            employeesMin={employeesMin}
            employeesMax={employeesMax}
            setEmployeesMin={setEmployeesMin}
            setEmployeesMax={setEmployeesMax}
            showFinancialYear={showFinancialYear}
            yearPickerOpen={yearPickerOpen}
            setYearPickerOpen={setYearPickerOpen}
            financialYear={financialYear}
            setFinancialYear={setFinancialYear}
            yearOptions={yearOptions}
            facetCount={facetCount}
            hasYearlyMetadata={hasYearlyMetadata}
            setHasYearlyMetadata={setHasYearlyMetadata}
          />

          {/* Group 5 — People: person name + avg board birth-year age */}
          {!hidePersonSearch && (
            <PeopleFilterGroup
              t={t}
              peopleCount={peopleCount}
              clearPeople={clearPeople}
              openFilterGroup={openFilterGroup}
              handleGroupOpenChange={handleGroupOpenChange}
              groupLock={groupLock}
              lockOf={lockOf}
              personName={personName}
              setPersonName={setPersonName}
              handleFormSubmit={handleFormSubmit}
              ageOpen={ageOpen}
              setAgeOpen={setAgeOpen}
              selectedAgeRanges={selectedAgeRanges}
              setSelectedAgeRanges={setSelectedAgeRanges}
              getAgeRangeLabel={getAgeRangeLabel}
              toggleSet={toggleSet}
              facets={facets}
              familyManaged={familyManaged}
              setFamilyManaged={setFamilyManaged}
              facetCount={facetCount}
              founderLed={founderLed}
              setFounderLed={setFounderLed}
              successionRisk={successionRisk}
              setSuccessionRisk={setSuccessionRisk}
            />
          )}

          {/* Group 6 — Register & identifiers: euId, registerId, registerCourt,
              registerType. The unified keyword input (q) sits above all groups
              at the top of the sidebar — see the block right under the
              "Scrollable Filters" comment. */}
          <RegisterFilterGroup
            t={t}
            registerCount={registerCount}
            clearRegister={clearRegister}
            openFilterGroup={openFilterGroup}
            handleGroupOpenChange={handleGroupOpenChange}
            groupLock={groupLock}
            currentEuid={currentEuid}
            setCurrentEuid={setCurrentEuid}
            handleFormSubmit={handleFormSubmit}
            currentRegisterId={currentRegisterId}
            setCurrentRegisterId={setCurrentRegisterId}
            registerCourtOpen={registerCourtOpen}
            setRegisterCourtOpen={setRegisterCourtOpen}
            setRegisterCourtSearch={setRegisterCourtSearch}
            currentRegisterCourt={currentRegisterCourt}
            i18n={i18n}
            setCurrentRegisterCourt={setCurrentRegisterCourt}
            registerCourtSearch={registerCourtSearch}
            courtList={courtList}
            facetCount={facetCount}
            registerTypeOpen={registerTypeOpen}
            setRegisterTypeOpen={setRegisterTypeOpen}
            currentRegisterType={currentRegisterType}
            setCurrentRegisterType={setCurrentRegisterType}
            registerTypeOptions={registerTypeOptions}
            leiCode={leiCode}
            setLeiCode={setLeiCode}
            lockOf={lockOf}
            ustIdnr={ustIdnr}
            setUstIdnr={setUstIdnr}
          />

        </div>

        {/* Footer Actions */}
        <SidebarFooter
          onSaveSearch={onSaveSearch}
          collectFilters={collectFilters}
          savedSearches={savedSearches}
          isCurrentSearchSaved={isCurrentSearchSaved}
          t={t}
          onLoadSavedSearch={onLoadSavedSearch}
          handleFormSubmit={handleFormSubmit}
          tier={tier}
          onOffsetChange={onOffsetChange}
          offset={offset}
          totalLimit={totalLimit}
          total={total}
        />
      </div>
    </aside>
  );
}
